import React, { useState, type FC } from 'react';

import { Button, NavLinkButton, Panel, Alert } from '@shared/components/bootstrap';
import { UserRole } from '@shared/types/user_role';

import { InputFormGroup } from '@admin/components/fields/input_form_group';
import { SelectFormGroup } from '@admin/components/fields/select_form_group';
import { ROLES } from '@admin/config/roles';

export type LocationFormData = {
  type?: string;
  value?: string /* barcode */;
  name?: string;
  aisle?: string;
  stack?: string;
  row?: string;
  level?: string;
  bay?: string;
  width?: string;
  depth?: string;
  height?: string;
  capacity?: string;
  directable_kind?: string;
  damaged: boolean;
  inactive: boolean;
  is_full: boolean;
  skip_capacity_validation: boolean;
};

export const LocationForm: FC<{
  loading?: boolean;
  error?: string;
  data?: LocationFormData;
  onSave(data: LocationFormData): void;
}> = ({ loading, error, data, onSave }) => {
  const admin = ROLES.has(UserRole.Admin);
  const manager = ROLES.has(UserRole.Manager);
  const [type, setType] = useState<string | undefined>(data?.type);
  const [value, setValue] = useState<string | undefined>(data?.value);
  const [name, setName] = useState<string | undefined>(data?.name);
  const [aisle, setAisle] = useState<string | undefined>(data?.aisle);
  const [stack, setStack] = useState<string | undefined>(data?.stack);
  const [row, setRow] = useState<string | undefined>(data?.row);
  const [level, setLevel] = useState<string | undefined>(data?.level);
  const [bay, setBay] = useState<string | undefined>(data?.bay);
  const [width, setWidth] = useState<string | undefined>(data?.width);
  const [depth, setDepth] = useState<string | undefined>(data?.depth);
  const [height, setHeight] = useState<string | undefined>(data?.height);
  const [capacity, setCapacity] = useState<string | undefined>(data?.capacity);
  const [directable_kind, setDirectableKind] = useState<string | undefined>(data?.directable_kind);
  const [damaged, setDamaged] = useState<boolean>(data?.damaged ?? false);
  const [inactive, setInactive] = useState<boolean>(data?.inactive ?? false);
  const [is_full, setIsFull] = useState<boolean>(data?.is_full ?? false);
  const [skip_capacity_validation, setSkipCapacityValidation] = useState<boolean>(
    data?.skip_capacity_validation ?? false,
  );

  const onSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    event.stopPropagation();
    if (!type) return;
    onSave({
      type,
      value,
      name,
      aisle,
      stack,
      row,
      level,
      bay,
      width,
      depth,
      height,
      capacity,
      directable_kind,
      damaged,
      inactive,
      is_full,
      skip_capacity_validation,
    });
  };

  return (
    <form onSubmit={onSubmit}>
      {error && <Alert style="danger">{error}</Alert>}
      <Panel>
        <Panel.Header>
          <Panel.Title>Details</Panel.Title>
        </Panel.Header>

        <div className="panel-body">
          <SelectFormGroup
            disabled={!admin}
            id="location_type"
            name="location[type]"
            label="Type:"
            value={type ?? ''}
            onChange={(event) => setType(event.target.value || undefined)}
          >
            <option value=""> - Type - </option>
            <option value="shelf">Shelf</option>
            <option value="staging">Staging</option>
            <option value="cargo">Cargo</option>
            <option value="dock">Dock</option>
            <option value="pnd">Pnd</option>
            <option value="overflow">Overflow</option>
          </SelectFormGroup>

          <InputFormGroup
            type="text"
            id="location_barcode"
            name="location[barcode]"
            label="Barcode:"
            value={value ?? ''}
            onChange={(event) => setValue(event.target.value || undefined)}
            disabled={!admin}
          />

          <InputFormGroup
            type="text"
            id="location_name"
            name="location[name]"
            label="Name:"
            value={name ?? ''}
            onChange={(event) => setName(event.target.value || undefined)}
            disabled={!admin}
          />

          <InputFormGroup
            type="text"
            id="location_aisle"
            name="location[aisle]"
            label="Aisle:"
            placeholder="optional"
            value={aisle ?? ''}
            onChange={(event) => setAisle(event.target.value || undefined)}
            disabled={!admin}
          />

          <InputFormGroup
            type="text"
            id="location_stack"
            name="location[stack]"
            label="Stack:"
            placeholder="optional"
            value={stack ?? ''}
            onChange={(event) => setStack(event.target.value || undefined)}
            disabled={!admin}
          />

          <InputFormGroup
            type="text"
            id="location_row"
            name="location[row]"
            label="Row:"
            placeholder="optional"
            value={row ?? ''}
            onChange={(event) => setRow(event.target.value)}
            disabled={!admin}
          />

          <InputFormGroup
            type="text"
            id="location_level"
            name="location[level]"
            label="Level:"
            placeholder="optional"
            value={level ?? ''}
            onChange={(event) => setLevel(event.target.value || undefined)}
            disabled={!admin}
          />

          <InputFormGroup
            type="text"
            id="location_bay"
            name="location[bay]"
            label="Bay:"
            placeholder="optional (will default to 1 for Shelf locations)"
            value={bay ?? ''}
            onChange={(event) => setBay(event.target.value || undefined)}
            disabled={!admin}
          />

          <InputFormGroup
            type="text"
            id="location_width"
            name="location[width]"
            label="Width (inches):"
            placeholder="optional for non-Overflow locations (will default to 44 inches * capacity for Shelf locations)"
            value={width ?? ''}
            onChange={(event) => setWidth(event.target.value || undefined)}
            disabled={!admin}
          />

          <InputFormGroup
            type="text"
            id="location_depth"
            name="location[depth]"
            label="Depth (inches):"
            placeholder="optional for non-Overflow locations (will default to 48 inches for Shelf locations)"
            value={depth ?? ''}
            onChange={(event) => setDepth(event.target.value || undefined)}
            disabled={!admin}
          />

          <InputFormGroup
            type="text"
            id="location_height"
            name="location[height]"
            label="Height (inches):"
            placeholder="optional for non-Overflow and non-Shelf locations"
            value={height ?? ''}
            onChange={(event) => setHeight(event.target.value || undefined)}
            disabled={!admin}
          />

          <InputFormGroup
            type="text"
            id="location_capacity"
            name="location[capacity]"
            label="Capacity:"
            placeholder="optional for non-Shelf locations and for Shelf locations with loose items"
            value={capacity ?? ''}
            onChange={(event) => setCapacity(event.target.value || undefined)}
            disabled={!admin}
          />

          <InputFormGroup
            type="text"
            id="location_directable_kind"
            name="location[directable_kind]"
            label="Directable Kind:"
            placeholder="optional e.g. long_rug"
            value={directable_kind ?? ''}
            onChange={(event) => setDirectableKind(event.target.value || undefined)}
            disabled={!admin}
          />

          <InputFormGroup
            type="checkbox"
            id="location_damaged"
            name="location[damaged]"
            label="Damaged:"
            checked={!!damaged}
            onChange={(event) => setDamaged(event.target.checked)}
            disabled={!admin}
          />

          <InputFormGroup
            type="checkbox"
            id="location_inactive"
            name="location[inactive]"
            label="Inactive:"
            checked={!!inactive}
            onChange={(event) => setInactive(event.target.checked)}
            disabled={!admin && !manager}
          />

          <InputFormGroup
            type="checkbox"
            id="location_is_full"
            name="location[is_full]"
            label="Is Full:"
            checked={!!is_full}
            onChange={(event) => setIsFull(event.target.checked)}
            disabled={!admin && !manager}
          />

          <InputFormGroup
            type="checkbox"
            id="location_skip_capacity_validation"
            name="location[skip_capacity_validation]"
            label="Loose Items (Oversized Pallets) Only:"
            checked={!!skip_capacity_validation}
            onChange={(event) => setSkipCapacityValidation(event.target.checked)}
            disabled={!admin || !type}
          />
        </div>

        <Panel.Footer align="right">
          <NavLinkButton kind="default" to="/warehouses">
            Cancel
          </NavLinkButton>
          <Button kind="primary" type="submit" disabled={!value} loading={loading}>
            Save
          </Button>
        </Panel.Footer>
      </Panel>
    </form>
  );
};
